import React, { useEffect, useState } from 'react';
import { Backdrop, Button, CircularProgress, Modal } from '@mui/material';
import styles from '../MiniSizingDialog/MiniSizingDialog.module.scss';
import { useTranslation } from 'react-i18next';
import { GetSystemResponse, Part } from '../../models/Api';
import SystemService from '../../services/SystemService';
import SpinnerService, { SpinnerStatusType } from '../../services/SpinnerService';
import { copyMetadataToLinkedPart, isManifoldOptimized, confirmManifoldRevert } from '../../utils/Utils';

function MiniSizingDialog(props: {
  part: Part;
  showMiniSizingModal: boolean;
  createPartSizingData: boolean;
  closeMiniSizingModal: () => void;
  system: GetSystemResponse | undefined;
}) {
  const { t } = useTranslation();
  const { part, showMiniSizingModal, createPartSizingData, closeMiniSizingModal, system } = props;
  const miniSizingUrl = `${process.env.REACT_APP_MINI_SIZING_URL}${part?.workflowId}&skipLinkedPartCheck=True`;
  const [showOptimizedManifoldAlert, setShowOptimizedManifoldAlert] = useState(false);
  const [revertManifold, setRevertManifold] = useState(false);
  const [showSpinner, setShowSpinner] = useState<SpinnerStatusType>({
    pendingApiResponses: 0,
    isIframeLoaded: true,
  });
  const overlapGroup = props.system?.overlapGroupLinks?.find((overlapGroupobj) => overlapGroupobj.overlappingGroupId === part.groupInstanceId);
  const manifoldGroupId = system?.groups?.find((groupObj) => groupObj.id === overlapGroup?.intersectedGroupId)?.parentGroupId as number;
  const postMessage = (type: string, data: string) => {
    const iFrameElement: HTMLIFrameElement = document?.getElementById('miniSizingContainer') as HTMLIFrameElement;
    iFrameElement?.contentWindow?.postMessage({ type: type, data: data }, '*');
  };
  const submitPartData = async () => {
    if (createPartSizingData) {
      await SystemService.createPartSizingData(part);
    }
    const handleNonParkerPartSavedMessage = async (event: MessageEvent) => {
      if (event.data.type === 'nonParkerPartSaved') {
        await SystemService.getPartsDetail(part.id as string);
        window.removeEventListener('message', handleNonParkerPartSavedMessage);
        closeMiniSizingModal();
        const copyInfo = copyMetadataToLinkedPart(part.id as string);
        if (copyInfo?.targetPartId) {
          await SystemService.updatePartMetadata(copyInfo.targetPartId, copyInfo.metadataInstances);
        }
      }
    };
    window.addEventListener('message', handleNonParkerPartSavedMessage);
    postMessage('saveNonParkerPart', '');
  };
  useEffect(() => {
    SpinnerService.getSpinnerStatus(setShowSpinner);
  }, []);
  useEffect(() => {
    if (revertManifold) submitPartData();
  }, [revertManifold]);

  return (
    <>
      <Modal open={showMiniSizingModal} onClose={() => closeMiniSizingModal()}>
        <div className={styles['mini-sizing-dialog']}>
          <div>
            {!showSpinner.isIframeLoaded && (
              <Backdrop color="secondary" open={!showSpinner.isIframeLoaded} className={styles['mini-sizing-loader']}>
                <CircularProgress color="primary" />
              </Backdrop>
            )}
            <iframe
              id="miniSizingContainer"
              src={miniSizingUrl}
              className={styles['mini-sizing-iframe']}
              onLoad={() => {
                postMessage('ready', 'parent');
                SpinnerService.updateSpinnerStatus(0, true);
              }}
            ></iframe>
          </div>
          <div className={styles['dialog-buttons']}>
            <Button
              onClick={async () => {
                if (isManifoldOptimized(manifoldGroupId)) {
                  setShowOptimizedManifoldAlert(true);
                  return;
                }
                submitPartData();
              }}
            >
              {t('OK')}
            </Button>

            <Button
              onClick={() => {
                closeMiniSizingModal();
              }}
            >
              {t('Cancel')}
            </Button>
          </div>
        </div>
      </Modal>
      {showOptimizedManifoldAlert && confirmManifoldRevert(showOptimizedManifoldAlert, setShowOptimizedManifoldAlert, setRevertManifold, manifoldGroupId)}
    </>
  );
}

export default MiniSizingDialog;
